'use client'

import Link from 'next/link'
import { useState } from 'react'
import { useTranslations } from 'next-intl'
import { useAction, useQuery } from 'convex/react'
import { makeFunctionReference } from 'convex/server'
import { api } from '../../../convex/_generated/api'
import type { Id } from '../../../convex/_generated/dataModel'
import { AlertCircle, CreditCard } from 'lucide-react'
import { Button } from '@/design-system'
import OnboardingCheckoutModal from '@/components/onboarding/OnboardingCheckoutModal'

// Reference initiateCheckout (not yet in generated API until codegen runs)
const lsInitiateCheckout = makeFunctionReference<
  'action',
  { planId?: Id<'plans'>; billingCycle?: 'monthly' | 'annually' },
  { checkoutUrl: string; trialEndsAt?: number }
>('features/billing/lsActions:initiateCheckout')

/**
 * Inline paywall rendered in place of page content when an organization is suspended.
 * Handles two cases:
 *  - No subscription (trial expired, never paid): shows checkout modal to subscribe
 *  - Has subscription (payment failed): opens LS portal to fix payment method
 */
export default function SuspensionPaywall() {
  const t = useTranslations('billing.suspensionPaywall')
  const tCommon = useTranslations('common')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false)

  const getBillingPortalUrl = useAction(api.features.billing.lsActions.getBillingPortalUrl)
  const subscription = useQuery(api.features.billing.queries.getSubscription)

  // true = has subscription (fix payment), false = no subscription (checkout), undefined = loading
  const hasSubscription = subscription !== undefined
    ? subscription !== null
    : undefined

  const handleFixPayment = async () => {
    setLoading(true)
    setError(null)
    const newTab = window.open('', '_blank')
    try {
      const { portalUrl } = await getBillingPortalUrl({})
      if (newTab) {
        newTab.location.href = portalUrl
      } else {
        window.open(portalUrl, '_blank', 'noopener,noreferrer')
      }
    } catch (err: any) {
      newTab?.close()
      setError(err?.data?.message ?? err?.message ?? 'Could not open billing portal')
    } finally {
      setLoading(false)
    }
  }

  const handleSubscribeNow = () => {
    setError(null)
    setIsCheckoutModalOpen(true)
  }

  // When checkout completes, the subscription_created webhook will unsuspend the org
  // and ConditionalLayout will reactively remove the paywall
  const handleCheckoutComplete = () => {
    setIsCheckoutModalOpen(false)
  }

  return (
    <>
      <div className="flex items-center justify-center min-h-[60vh] p-4">
        <div
          className="w-full max-w-md rounded-2xl p-8 text-center shadow-2xl"
          style={{
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
          }}
        >
          {/* Icon */}
          <div
            className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full"
            style={{ background: 'var(--color-pink-muted)' }}
          >
            <AlertCircle className="h-8 w-8" style={{ color: 'var(--color-pink)' }} />
          </div>

          {/* Title */}
          <h3
            className="text-xl font-bold mb-2"
            style={{ color: 'var(--color-text-primary)' }}
          >
            {t('accountPaused')}
          </h3>

          {/* Description */}
          <p
            className="text-sm mb-6"
            style={{ color: 'var(--color-text-muted)' }}
          >
            {hasSubscription
              ? t('paymentIssueDescription')
              : t('trialEndedDescription')}
          </p>

          {/* Actions */}
          <div className="flex flex-col gap-3">
            {hasSubscription === undefined ? (
              // Loading state
              <Button variant="default" className="w-full justify-center" disabled>
                <CreditCard className="w-4 h-4 mr-2" />
                {tCommon('loading')}
              </Button>
            ) : hasSubscription ? (
              // Has subscription → fix payment method via portal
              <Button
                variant="default"
                className="w-full justify-center gap-2"
                onClick={handleFixPayment}
                disabled={loading}
              >
                <CreditCard className="w-4 h-4" />
                {loading ? t('opening') : t('fixPaymentMethod')}
              </Button>
            ) : (
              // No subscription → subscribe via checkout
              <Button
                variant="default"
                className="w-full justify-center gap-2"
                onClick={handleSubscribeNow}
                disabled={loading}
              >
                <CreditCard className="w-4 h-4" />
                {t('subscribeNow')}
              </Button>
            )}

            {error && (
              <p className="text-xs" style={{ color: 'var(--color-pink)' }}>{error}</p>
            )}
            <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
              {t('needHelp')}{' '}
              <Link
                href="mailto:support@42a.ai"
                className="underline transition-colors"
                style={{ color: 'var(--color-lime)' }}
              >
                {t('contactUs')}
              </Link>
            </p>
          </div>
        </div>
      </div>

      <OnboardingCheckoutModal
        isOpen={isCheckoutModalOpen}
        onClose={() => setIsCheckoutModalOpen(false)}
        onComplete={handleCheckoutComplete}
      />
    </>
  )
}
